/**
 * Azure AD B2C (Microsoft Identity) authentication configuration for SSO.
 * Used by MSAL (Microsoft Authentication Library) in the web app.
 *
 * B2C uses policy-based authorities, e.g.:
 *   https://{tenantHost}.b2clogin.com/{domain}/{policyName}
 * The b2clogin host uses the domain prefix (e.g. linkgalaxydevtoazure from linkgalaxydevtoazure.onmicrosoft.com).
 */
const domain = import.meta.env.VITE_B2C_DOMAIN ?? 'yourtenant.onmicrosoft.com';
const signUpSignInPolicy = import.meta.env.VITE_B2C_SIGN_UP_SIGN_IN_POLICY ?? 'B2C_1_SignUpSignIn';
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID ?? 'f084dbfe-a681-4523-8371-749ac8020c07';

/** Callback path where B2C redirects after sign-in (e.g. /signin-oidc). Must match app registration Redirect URI. */
export const callbackPath = import.meta.env.VITE_B2C_CALLBACK_PATH ?? '/signin-oidc';

/** B2C host: domain prefix (e.g. linkgalaxydevtoazure from linkgalaxydevtoazure.onmicrosoft.com). */
const b2cHostName = domain.replace(/\.onmicrosoft\.com$/i, '');

/**
 * The B2C instance (scheme + host, no policy) that issues tokens for this app.
 *
 * Defaults to the `{tenant}.b2clogin.com` host derived from the domain, which is what this app
 * has always used. `VITE_B2C_INSTANCE` overrides it with a custom sign-in domain such as
 * `https://authenticate.openbots-dev.io`.
 *
 * WHY the override exists: B2C stamps `iss` with whichever host issued the token, and keeps a
 * separate session cookie per host. CoordioCore signs in through a custom domain, so while this
 * app derives `b2clogin.com` it lands on a DIFFERENT host, gets a DIFFERENT issuer, and cannot
 * see CoordioCore's session — which is why the user is asked to sign in a second time. One
 * shared host is the precondition for single sign-on between the two apps; without this variable
 * the host could not be expressed at all.
 *
 * Setting it alone is NOT sufficient: `VITE_AZURE_CLIENT_ID` and `VITE_B2C_API_SCOPE` must move
 * to the shared app registration too, and the API must accept the resulting issuer and audience.
 */
const b2cInstance =
  String(import.meta.env.VITE_B2C_INSTANCE ?? '').trim().replace(/[/]+$/, '') ||
  `https://${b2cHostName}.b2clogin.com`;

/** B2C authority for sign-up/sign-in (used for login). */
export const b2cAuthority = `${b2cInstance}/${domain}/${signUpSignInPolicy}`;

/** Full redirect URI: origin + callback path (used when VITE_AZURE_REDIRECT_URI is not set). */
const defaultRedirectUri =
  typeof window !== 'undefined' ? `${window.location.origin}${callbackPath.startsWith('/') ? callbackPath : `/${callbackPath}`}` : '';

export const msalConfig = {
  auth: {
    clientId,
    authority: b2cAuthority,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI ?? defaultRedirectUri,
    postLogoutRedirectUri: import.meta.env.VITE_AZURE_POST_LOGOUT_URI ?? (typeof window !== 'undefined' ? window.location.origin : ''),
    navigateToLoginRequestUrl: true,
    /** B2C: required so MSAL trusts the B2C issuer. */
    knownAuthorities: [b2cInstance],
  },
  cache: {
    cacheLocation: 'sessionStorage' as const,
    /** true = store PKCE state in cookie so it survives redirect (recommended for B2C SPA). */
    storeAuthStateInCookie: true,
  },
};

/**
 * Scopes to request when acquiring a token for the AgentOps API.
 * In B2C, the API app registration exposes scopes (e.g. https://yourtenant.onmicrosoft.com/api/read).
 * Use the full scope URI from your B2C API app registration.
 */
const apiScopeUri = import.meta.env.VITE_B2C_API_SCOPE ?? `https://${domain}/api/read`;
export const apiScope = apiScopeUri;

/**
 * Request for login. In B2C, the API scope must be requested at login so the user
 * consents and MSAL can later silently acquire an access token for the API.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', apiScope],
};

/**
 * Request scopes when calling the AgentOps API (access token for the API).
 */
export const apiRequest = {
  scopes: [apiScope],
};
